import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ShieldCheck,
  Search,
  Zap,
  Save,
  AlertTriangle,
  Archive,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { useAuth } from "@/contexts/AuthContext";

interface SessionOption {
  sessionId: string;
  studentId: string;
  studentName: string;
  phoneNumber: string;
  stage: string | null;
  lastStudentMessageAt: string | null;
  daysQuiet: number | null;
  isArchived: boolean;
  allowed: boolean;
}

interface RunResult {
  dryRun: boolean;
  scanned: number;
  selected: number;
  skippedNoFit: number;
  skippedLowConfidence: number;
  skippedVetoed: number;
  sent: number;
  failed: number;
  errors: number;
  candidates?: Array<{
    sessionId: string;
    chosenTemplate: string | null;
    reasoning: string;
    confidence: number;
    errorMessage?: string;
  }>;
}

/**
 * Controls which real students automated re-engagement is permitted to message.
 *
 * The reason this exists: arming sends without an allowlist means the next
 * scheduled run messages every quiet student on the platform. For testing
 * against production chats you want the opposite — a short, explicit list of
 * your own conversations.
 */
export default function ReengagementSettings() {
  // Saving the allowlist and firing a run both decide who receives a real
  // WhatsApp message, so the backend restricts them to super admins. Hide the
  // whole panel from regular admins rather than showing controls that 403 —
  // there is nothing here worth reading without the ability to act on it.
  const { isSuperAdmin } = useAuth();
  const [sessions, setSessions] = useState<SessionOption[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [restrict, setRestrict] = useState(true);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<RunResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsResp, sessionsResp] = await Promise.all([
        api.get<any>("/admin/whatsapp/reengagement/settings"),
        api.get<any>("/admin/whatsapp/reengagement/sessions"),
      ]);
      const settings = settingsResp?.data ?? settingsResp;
      const list: SessionOption[] = sessionsResp?.data ?? sessionsResp ?? [];
      setRestrict(settings?.restrictToAllowlist ?? true);
      setSelected(new Set(settings?.allowedSessionIds ?? []));
      setSessions(Array.isArray(list) ? list : []);
      setDirty(false);
    } catch (e: any) {
      setError(e?.message || "Could not load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter(
      (s) =>
        s.studentName.toLowerCase().includes(q) ||
        s.phoneNumber.toLowerCase().includes(q),
    );
  }, [sessions, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setDirty(true);
    setNotice(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put("/admin/whatsapp/reengagement/settings", {
        restrictToAllowlist: restrict,
        allowedSessionIds: Array.from(selected),
      });
      setDirty(false);
      setNotice(
        restrict
          ? `Saved. Only these ${selected.size} chat${selected.size === 1 ? "" : "s"} can receive automated templates.`
          : "Saved. Every eligible student can now be messaged — the allowlist is off.",
      );
    } catch (e: any) {
      setError(e?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async (dryRun: boolean) => {
    if (!selected.size) return;
    if (!dryRun) {
      const ok = window.confirm(
        `Send real WhatsApp messages to ${selected.size} student${selected.size === 1 ? "" : "s"} now?\n\n` +
          `This picks a template per chat and sends it immediately. It cannot be undone.`,
      );
      if (!ok) return;
    }
    setRunning(true);
    setError(null);
    setNotice(null);
    setLastRun(null);
    try {
      const resp = await api.post<any>(
        "/admin/whatsapp/reengagement/run-for-sessions",
        { sessionIds: Array.from(selected), dryRun },
      );
      setLastRun(resp?.data ?? resp);
    } catch (e: any) {
      setError(e?.message || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const selectedArchived = sessions.filter(
    (s) => selected.has(s.sessionId) && s.isArchived,
  ).length;

  if (!isSuperAdmin) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-start gap-3 mb-1">
        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Automated re-engagement — allowed chats
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Pick which conversations the system may message when a student goes
            quiet. This applies to the daily job as well as the button below.
          </p>
        </div>
      </div>

      {/* Restriction toggle — the single most consequential control here. */}
      <div
        className={cn(
          "mt-4 rounded-md border p-3 flex items-start gap-3",
          restrict
            ? "border-blue-200 bg-blue-50"
            : "border-amber-300 bg-amber-50",
        )}
      >
        <input
          id="restrict"
          type="checkbox"
          checked={restrict}
          onChange={(e) => {
            setRestrict(e.target.checked);
            setDirty(true);
            setNotice(null);
          }}
          className="mt-0.5 h-4 w-4"
        />
        <label htmlFor="restrict" className="text-sm cursor-pointer">
          <span className="font-medium text-gray-900">
            Only message the chats selected below
          </span>
          <span className="block text-gray-600 mt-0.5">
            {restrict ? (
              <>
                Recommended while testing. With this off, the daily job may
                message <strong>every</strong> eligible student.
              </>
            ) : (
              <>
                <AlertTriangle className="inline h-3.5 w-3.5 mr-1 -mt-0.5 text-amber-600" />
                The allowlist is off — every eligible student can be messaged.
              </>
            )}
          </span>
        </label>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {notice}
        </div>
      )}

      {/* Search */}
      <div className="relative mt-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or phone number"
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="mt-3 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {visible.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                No WhatsApp conversations found.
              </div>
            ) : (
              visible.map((s) => {
                const checked = selected.has(s.sessionId);
                return (
                  <label
                    key={s.sessionId}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50",
                      checked && "bg-blue-50/50",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(s.sessionId)}
                      className="h-4 w-4 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {s.studentName}
                        </span>
                        {s.isArchived && (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200"
                            title="Archived leads are never messaged, even if selected"
                          >
                            <Archive className="h-3 w-3" />
                            Archived
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {s.phoneNumber || "no number"}
                        {s.daysQuiet !== null && ` · quiet ${s.daysQuiet}d`}
                        {s.stage && ` · ${s.stage.replace(/_/g, " ").toLowerCase()}`}
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>

          {/* Archived leads are excluded server-side; say so rather than
              letting someone select one and wonder why nothing happened. */}
          {selectedArchived > 0 && (
            <p className="mt-2 text-xs text-amber-700 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              {selectedArchived} selected chat{selectedArchived === 1 ? " is" : "s are"} archived
              and will be skipped — archived leads are never messaged.
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600 mr-auto">
              {selected.size} chat{selected.size === 1 ? "" : "s"} selected
            </span>

            <button
              onClick={save}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </button>

            <button
              onClick={() => runNow(true)}
              disabled={running || !selected.size}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
            >
              {running ? "Running…" : "Preview (no send)"}
            </button>

            <button
              onClick={() => runNow(false)}
              disabled={running || !selected.size}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
            >
              <Zap className="h-4 w-4" />
              {running ? "Sending…" : "Send now"}
            </button>
          </div>

          <p className="mt-2 text-xs text-gray-500">
            Both buttons pick a template per chat from its conversation. “Preview”
            shows the choice and reasoning without messaging anyone. Selected
            chats skip the usual 3-day wait and cooldown, since you chose them
            deliberately.
          </p>

          {lastRun && (
            <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center gap-2 mb-2">
                {lastRun.dryRun ? (
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200">
                    Preview — nothing sent
                  </span>
                ) : (
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">
                    Live run
                  </span>
                )}
                <span className="text-xs text-gray-600">
                  {lastRun.scanned} scanned · {lastRun.selected} matched
                  {!lastRun.dryRun && ` · ${lastRun.sent} sent`}
                  {!lastRun.dryRun && lastRun.failed > 0 && ` · ${lastRun.failed} failed`}
                </span>
              </div>

              {lastRun.skippedNoFit > 0 && (
                <p className="text-xs text-gray-600 mb-2">
                  {lastRun.skippedNoFit} chat{lastRun.skippedNoFit === 1 ? "" : "s"} had no
                  suitable template — a replacement has been drafted for review.
                </p>
              )}

              <ul className="space-y-1.5">
                {(lastRun.candidates ?? []).map((c) => {
                  const student = sessions.find((s) => s.sessionId === c.sessionId);
                  return (
                    <li key={c.sessionId} className="text-xs flex items-start gap-2">
                      {c.errorMessage ? (
                        <XCircle className="h-3.5 w-3.5 mt-0.5 text-red-500 shrink-0" />
                      ) : c.chosenTemplate ? (
                        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-600 shrink-0" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 mt-0.5 text-gray-400 shrink-0" />
                      )}
                      <span className="text-gray-700">
                        <strong>{student?.studentName ?? "Student"}</strong>
                        {" — "}
                        {c.chosenTemplate ? (
                          <>
                            <code className="bg-white border border-gray-200 rounded px-1">
                              {c.chosenTemplate}
                            </code>{" "}
                            <span className="text-gray-500">
                              ({Math.round(c.confidence * 100)}% confident)
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-500">no suitable template</span>
                        )}
                        <span className="block text-gray-500 mt-0.5">{c.reasoning}</span>
                        {c.errorMessage && (
                          <span className="block text-red-600 mt-0.5">
                            Send failed: {c.errorMessage}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
